import { Fragment } from 'react';
import {
  PaymentOption,
  PaymentMethodId,
  PaymentStatus,
  RefType,
  PaymentEntryType,
  PaymentTransactionEntry,
  PaymentMethodTypeEnum,
  BankAccountUsage,
  PaymentTransaction,
  PaymentProviderCode,
} from '@commerce-payment-webcomponents/sdk';
import { FAIcon, LoaderType, NextgenImage, Theme, useTranslation } from '@nextgen-web-framework/all';
import { formatPrice, uniqueId } from '@nextgen-web-framework/core';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { parseInt } from 'lodash';
import { JSX } from 'react/jsx-runtime';
import { ChannelType } from '@commerce-payment-webcomponents/sdk/services/models/ChannelType';
import { getPaymentOptionByID, getTransactionCardInfo } from '../../../common/Utils';
import { ImageWrapper } from '../ImageWrapper/ImageWrapper';
import { Images } from '../../../common/Constant';
import styles from './PaymentDetail.module.scss';
import { PaymentDetailProps } from './types/propsTypes';

export const PaymentDetail = (props: PaymentDetailProps) => {
  /* eslint react/prop-types: 0 */ //no feedback from framework team to resolve the prop check issue
  const {
    paymentOptions,
    transactions,
    hidePaymentSummaryAmount,
    orderType,
    enableDirectDebit,
    isCardId,
    cardInfo,
    filterCategory,
    filterType,
    bankAccountDetails,
  } = props;
  const t = useTranslation();

  function renderIcon(cardSchemeCode?: string, icon?: string) {
    if (cardSchemeCode) {
      return <ImageWrapper img={cardSchemeCode.toLowerCase()} alt={cardSchemeCode} />;
    } else {
      return <FAIcon iconName={icon as IconName} theme={Theme.PURPLE} className={`${styles['payment-icon']}`} />;
    }
  }

  function renderIconTransaction(transaction?: PaymentTransaction, pmp?: PaymentOption) {
    if (transaction?.paymentMethodCode === PaymentMethodId.PayLaterCash) {
      return (
        <div className={`${styles['wrapper-image']}`}>
          <NextgenImage
            alt="efecty"
            src={Images.PAY_LATER_CASH}
            isRelativeURL={true}
            loaderType={LoaderType.WEBFONTS}
          />
        </div>
      );
    }
    // For DownLine payment
    if (transaction?.paymentMethodCode === PaymentMethodId.PayLaterQr && transaction?.isDownLinePayment) {
      return <FAIcon iconName={Images.DOWNLINE_PAYMENT as IconName} />;
    }
    return (
      <FAIcon
        iconName={
          (pmp?.icon as IconName) ||
          (transaction?.paymentMethodCode && (Images[transaction?.paymentMethodCode as keyof object] as IconName))
        }
      />
    );
  }

  function renderCreditCardOption({
    card,
    pmp,
    amount = 0,
    paymentChannel,
  }: {
    card?: {
      cardNo: string;
      cardSchemeCode: string;
      noOfInstallments?: number;
      interestRate?: number;
    };
    pmp: PaymentOption;
    amount: number;
    paymentChannel?: string;
  }) {
    const methodLabel = t(`payment.paymentMethods.${pmp?.paymentMethod.code}`);
    return (
      <div
        data-testid="payment-details"
        className={`${styles['user-payment-method']}`}
        key={pmp?.paymentMethodProviderId}
      >
        <div className={styles['header']}>
          <div className={`${styles['image-container']} ${styles['credit-card']}`}>
            {renderIcon(card?.cardSchemeCode, pmp?.icon)}
          </div>
          <div className={`${styles['label-wrapper']}`}>
            <div className={`${styles['pmp-label']}`}>{methodLabel}</div>
            {card?.noOfInstallments ? (
              <div className={`${styles['credit-card-no']}`}>
                {card?.noOfInstallments}
                {t('payment.summary.monthly')} ({card?.interestRate}
                {t('payment.summary.prec.interest')})
              </div>
            ) : (
              ''
            )}
            <div className={`${styles['credit-card-no']}`}>{card?.cardNo}</div>
            {paymentChannel === ChannelType.Pos && (
              <div className={styles['pay-at-shop']}>{t('payment.paymentDetails.PayAtShop')}</div>
            )}
          </div>
        </div>
        {!hidePaymentSummaryAmount && !isCardId && (
          <div data-testid="payment-amount" className={styles['amount']}>{`${formatPrice(amount)}`}</div>
        )}
      </div>
    );
  }

  const renderLabel = (pmp?: PaymentOption, transaction?: PaymentTransaction) => {
    // if paymentProviderCode is CNB show always dorect debit ATNG-5092
    if (transaction?.paymentProviderCode === PaymentProviderCode.CNB) {
      return t(`payment.paymentMethods.TH_PAY_LATER_CNB`);
    }
    //logic for DownLine PMP
    if (pmp?.paymentMethod.code === PaymentMethodId.PayLaterQr && transaction?.isDownLinePayment) {
      return t(`payment.paymentMethods.DOWNLINE_PAYMENT`);
    }
    return t(`payment.paymentMethods.${pmp?.paymentMethod.code || transaction?.paymentMethodCode}`);
  };

  function renderNormalOption(
    pmp?: PaymentOption,
    amount?: number,
    transaction?: PaymentTransaction,
    paymentChannel?: string,
  ) {
    return (
      <div
        data-testid="payment-details"
        className={`${styles['user-payment-method']}`}
        key={pmp?.paymentMethodProviderId || transaction?.paymentMethodProviderId}
      >
        <div className={styles['header']}>
          <div className={`${styles['image-container']}`}>{renderIconTransaction(transaction, pmp)}</div>
          <div className={`${styles['label-wrapper']}`}>
            <div className={`${styles['pmp-label']}`}>
              {renderLabel(pmp, transaction)}

              {/* //showing for payLater qr and not isDownLinePayment */}
              {pmp?.paymentMethod.code === PaymentMethodId.PayLaterQr &&
                !transaction?.isDownLinePayment &&
                transaction?.status !== PaymentStatus.Captured && (
                  <span className={styles['awaited-label']}>&nbsp;{t('payment.orderDetails.awaitedLabel')}</span>
                )}
            </div>

            {paymentChannel === ChannelType.Pos && (
              <div className={styles['pay-at-shop']}>{t('payment.paymentDetails.PayAtShop')}</div>
            )}
          </div>
        </div>

        {!hidePaymentSummaryAmount && (
          <div data-testid="payment-amount" className={styles['amount']}>
            {amount && `${formatPrice(amount)}`}
          </div>
        )}
      </div>
    );
  }

  function renderIppOption(transactionEntry: PaymentTransactionEntry | undefined) {
    return (
      <div data-testid="ipp-options" className={`${styles['user-payment-method']}`} key={transactionEntry?.entryId}>
        <div className={styles['header']}>
          <div className={`${styles['ipp-label-wrapper']}`}>
            <div className={`${styles['pmp-label']}`}>{t('payment.paymentMethods.instalmentOption')}</div>
            <div className={`${styles['label-wrapper']}`}>
              <div className={`${styles['pmp-label']}`}>
                {parseInt(transactionEntry?.payload?.installmentPeriod)} X{' '}
                {transactionEntry?.payload?.monthlyPayment
                  ? formatPrice(Number(transactionEntry?.payload?.monthlyPayment.replace(/,/g, '')))
                  : ''}
                {' ('}
                {transactionEntry?.payload?.interestType === 'M' ? 0 : transactionEntry?.payload?.interestRate}
                {t('payment.summary.prec.interest')}
                {')'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userPaymentForCard = (transaction: PaymentTransaction, pmp: PaymentOption | undefined, amountValue: number) => {
    const pmpWithTransaction = {
      ...pmp,
      paymentMethodProviderId: transaction.transactionId,
    } as PaymentOption;

    const card = getTransactionCardInfo(transaction, pmp);
    if (
      (orderType === RefType.Subscription && transaction.status === PaymentStatus.Verified) ||
      orderType !== RefType.Subscription
    ) {
      return renderCreditCardOption({
        card,
        pmp: pmpWithTransaction,
        amount: amountValue,
        paymentChannel: transaction?.paymentChannel,
      });
    }
    return '';
  };

  const userPaymentForInstallment = (
    transaction: PaymentTransaction,
    pmp: PaymentOption | undefined,
    amountValue: number,
  ) => {
    const card = getTransactionCardInfo(transaction, pmp);
    const paymentMethodProvider = {
      icon: 'calendar-check',
      paymentMethod: { code: PaymentMethodId.InstallmentPayment },
    } as PaymentOption;
    const captureEntry = transaction.entries?.find((e) => e.entryType == PaymentEntryType.Capture);
    const initEntry = transaction.entries?.find((e) => e.entryType == PaymentEntryType.Initiate);
    let content: JSX.Element = <></>;

    if (captureEntry) {
      content = (
        <div key={transaction?.paymentMethodProviderId}>
          {renderCreditCardOption({
            card,
            pmp: paymentMethodProvider,
            amount: amountValue,
            paymentChannel: transaction?.paymentChannel,
          })}

          {captureEntry?.payload?.installmentPeriod && <div>{renderIppOption(captureEntry)}</div>}
        </div>
      );
    } else if (initEntry) {
      content = renderCreditCardOption({
        card,
        pmp: paymentMethodProvider,
        amount: amountValue,
        paymentChannel: transaction?.paymentChannel,
      });
    }

    return content;
  };

  function renderUserPayment() {
    return transactions.map((transaction: PaymentTransaction) => {
      const pmp = getPaymentOptionByID(paymentOptions, transaction.paymentMethodCode ?? '');
      const amountValue = transaction.amount?.value ?? 0;
      // For CC
      if (transaction?.isPaymentTxn && pmp?.paymentMethod?.code === PaymentMethodId.CreditDebitCard) {
        return <Fragment key={transaction.transactionId}>{userPaymentForCard(transaction, pmp, amountValue)}</Fragment>;
      }
      //For IPP
      if (transaction?.isPaymentTxn && transaction?.paymentMethodCode === PaymentMethodId.InstallmentPayment) {
        return (
          <Fragment key={transaction.transactionId}>
            {userPaymentForInstallment(transaction, pmp, amountValue)}
          </Fragment>
        );
      }
      // FOR OTHER EXCEPT INTERNAL AND CASH
      if (
        transaction?.isPaymentTxn &&
        transaction?.paymentMethodType !== PaymentMethodTypeEnum.Internal &&
        transaction?.paymentMethodCode !== PaymentMethodId.Cash
      ) {
        return (
          <Fragment key={transaction.transactionId}>
            {renderNormalOption(pmp, amountValue, transaction, transaction?.paymentChannel)}
          </Fragment>
        );
      }
      return '';
    });
  }

  const renderMultipartTrxn = () => {
    const resultObj: {
      [x: string | number]: {
        value: number;
        paymentChannel: string;
        trxnObj: PaymentTransaction;
      };
    } = {};
    const resultTemplate: JSX.Element[] = [];

    transactions?.map((trxn) => {
      if (
        trxn.paymentMethodCode &&
        trxn.isPaymentTxn &&
        trxn?.amount?.value &&
        (trxn?.paymentMethodType === PaymentMethodTypeEnum.Internal || trxn.paymentMethodCode === PaymentMethodId.Cash)
      ) {
        if (resultObj[trxn.paymentMethodCode]?.value) {
          resultObj[trxn?.paymentMethodCode].value =
            Number(resultObj[trxn?.paymentMethodCode].value) + Number(trxn?.amount?.value);
          resultObj[trxn?.paymentMethodCode].paymentChannel =
            resultObj[trxn?.paymentMethodCode].paymentChannel === ChannelType.Pos ||
            trxn?.paymentChannel === ChannelType.Pos
              ? ChannelType.Pos
              : String(trxn?.paymentChannel);
        } else {
          resultObj[trxn.paymentMethodCode] = {
            value: Number(trxn?.amount?.value),
            paymentChannel: String(trxn?.paymentChannel),
            trxnObj: trxn,
          };
        }
      }
    });

    Object.keys(resultObj).forEach((key) =>
      resultTemplate.push(
        <Fragment key={resultObj[key].trxnObj.transactionId}>
          {renderNormalOption(
            undefined,
            resultObj[key].value ?? 0,
            resultObj[key].trxnObj,
            resultObj[key].paymentChannel,
          )}
        </Fragment>,
      ),
    );
    return resultTemplate;
  };

  const renderBankTransferPayment = () => {
    const bankOptionDetails = paymentOptions?.find(
      (option: PaymentOption) => option?.paymentMethod?.code === PaymentMethodId.OfflineBanking,
    );

    return (
      <div
        data-testid="payment-details"
        className={`${styles['user-payment-method']}`}
        key={bankOptionDetails?.paymentMethodProviderId}
      >
        <div className={styles['header']}>
          <div className={`${styles['image-container']}`}>
            <FAIcon iconName={bankOptionDetails?.icon as IconName} />
          </div>
          <div className={`${styles['label-wrapper']}`}>
            <div className={`${styles['pmp-label']}`}>
              {t(`payment.paymentMethods.${bankOptionDetails?.paymentMethod.code}`)}
            </div>
            <div className={styles['bank-details-value']}>{bankAccountDetails?.bankName}</div>
            <div className={styles['bank-details-value']}>{bankAccountDetails?.bankAccountNumber}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isCardId && cardInfo?.cardNo ? (
        <div className={styles['transaction-container']}>
          {renderCreditCardOption({
            card: cardInfo,
            pmp: {
              paymentProvider: { code: '', version: 0 },
              label: '',
              icon: '',
              paymentMethodProviderId: `${uniqueId()}`,
              paymentMethod: {
                code: PaymentMethodId.CreditDebitCard,
                type: PaymentMethodTypeEnum.External,
                version: 0,
              },
            },
            amount: 0,
          })}
        </div>
      ) : (
        ((paymentOptions?.length > 0 && transactions?.length > 0) || enableDirectDebit) && (
          <div className={styles['transaction-container']}>
            {/* Render for Internal PMP and CASH to club trxns */}
            {renderMultipartTrxn()}
            {/* Render other trxns except Internal PMP and CASH  */}
            {renderUserPayment()}
            {/* Render DIRECT_DEBIT_BANKING trxn code  */}
            {enableDirectDebit &&
              renderNormalOption(undefined, undefined, {
                paymentMethodCode: PaymentMethodId.DirectDebitBanking,
                paymentMethodProviderId: PaymentMethodId.DirectDebitBanking,
              } as PaymentTransaction)}
          </div>
        )
      )}
      {filterType === 'bank' && filterCategory === BankAccountUsage.Renewal && paymentOptions?.length > 0 && (
        <div className={styles['transaction-container']}>{renderBankTransferPayment()}</div>
      )}
    </>
  );
};
